import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/UnifiedHeader';
import MarketPulseCard from './components/MarketPulseCard';
import TalentMetricsChart from './components/TalentMetricsChart';
import RegulatoryTimeline from './components/RegulatoryTimeline';
import LocationComparison from './components/LocationComparison';
import MarketInsightCard from './components/MarketInsightCard';
import SubscriptionWidget from './components/SubscriptionWidget';
import TalentPoolAnalyzer from './components/TalentPoolAnalyzer';
import marketIntelligenceService from '../../services/marketIntelligenceService';
import { MARKET_OVERVIEW, CITIES, MARKET_DATA_SOURCES, DATA_AS_OF } from '../../constants/marketData';

const MarketIntelligenceHub = () => {
  const [activeTab, setActiveTab] = useState('pulse');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [regulatoryUpdates, setRegulatoryUpdates] = useState([]);
  const [complianceScores, setComplianceScores] = useState([]);
  const [upcomingChanges, setUpcomingChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch live data on component mount
  useEffect(() => {
    fetchMarketData();
    
    // Refresh data every hour
    const interval = setInterval(() => {
      fetchMarketData();
      setLastUpdated(new Date());
    }, 3600000); // 1 hour

    return () => clearInterval(interval);
  }, []);

  const fetchMarketData = async () => {
    try {
      setLoading(true);
      const data = await marketIntelligenceService.getAllMarketData();
      
      setRegulatoryUpdates(data.regulatory || []);
      setComplianceScores(data.compliance || []);
      setUpcomingChanges(data.upcoming || []);
      setLastUpdated(new Date(data.lastUpdated));
    } catch (error) {
      console.error('Error fetching market data:', error);
      // Fallback data will be used from service
    } finally {
      setLoading(false);
    }
  };

  // Market pulse — sourced from NASSCOM-Zinnov 2024 / Aon 2024
  const marketPulseData = [
    {
      title: 'Active India GCCs',
      value: MARKET_OVERVIEW.totalGccs.value,
      change: MARKET_OVERVIEW.yoyHeadcountGrowth.value,
      changeType: 'positive',
      icon: 'Building2',
      description: `Source: ${MARKET_OVERVIEW.totalGccs.source}`
    },
    {
      title: 'GCC Professionals',
      value: MARKET_OVERVIEW.totalEmployment.value,
      change: 'YoY headcount growth',
      changeType: 'positive',
      icon: 'Users',
      description: `Source: ${MARKET_OVERVIEW.totalEmployment.source}`
    },
    {
      title: 'Cost Savings vs US',
      value: MARKET_OVERVIEW.costSavingsVsUS.value,
      change: 'Typical range',
      changeType: 'positive',
      icon: 'TrendingDown',
      description: `Source: ${MARKET_OVERVIEW.costSavingsVsUS.source}`
    },
    {
      title: 'Industry Revenue (FY24)',
      value: MARKET_OVERVIEW.marketSize.value,
      change: MARKET_OVERVIEW.yoyRevenueGrowth.value + ' YoY',
      changeType: 'positive',
      icon: 'DollarSign',
      description: `Source: ${MARKET_OVERVIEW.marketSize.source}`
    }
  ];

  // Talent metrics — derived from CITIES.talentPool (LinkedIn Talent Insights 2024)
  // We render the 6 tier-1 hubs in the chart; tier-2 emerging hubs are surfaced in LocationComparison
  const talentMetricsData = CITIES.filter((c) => c.tier === 1).map((c) => {
    const pool = parseInt((c.talentPool || '0').replace(/[^0-9]/g, ''), 10) * 1000;
    return {
      city: c.name.replace(' (Bengaluru)', ''),
      available: pool,
      demand: Math.round(pool * 0.85)
    };
  });

  // Regulatory updates are fetched from live data via fetchMarketData()

  // Location comparison — derived from CITIES with talentPool (in 000s) and salary midpoint (LPA)
  const parseSalaryMidpointLpa = (range) => {
    const m = String(range || '').match(/([0-9.]+)[^0-9]+([0-9.]+)/);
    if (!m) return 0;
    return Math.round(((parseFloat(m[1]) + parseFloat(m[2])) / 2) * 10) / 10;
  };
  const locationData = CITIES.map((c) => ({
    city: c.name,
    talentPool: parseInt((c.talentPool || '0').replace(/[^0-9]/g, ''), 10),
    avgSalary: parseSalaryMidpointLpa(c.seniorEngineerSalary),
    infrastructure: c.infrastructureScore,
    costIndex: c.costIndex,
    timeZone: 'IST',
    tier: c.tier,
    state: c.state,
    notes: c.notes
  }));

  // Mock data for market insights
  const marketInsights = [
    {
      title: "AI Talent Surge in Indian GCC Market",
      summary: "Demand for AI specialists in GCCs has increased by 340% in the last quarter, with Bangalore leading the growth.",
      fullContent: `The Indian GCC market is experiencing an unprecedented surge in demand for AI specialists, with a remarkable 340% increase in job postings over the last quarter. Bangalore continues to dominate this space, accounting for 45% of all AI-related GCC positions.\n\nKey factors driving this growth include:\n• Increased investment in AI research and development\n• Growing adoption of machine learning in business processes\n• Expansion of existing GCCs into AI-driven services\n• Government initiatives supporting AI innovation\n\nCompanies are particularly seeking professionals with expertise in natural language processing, computer vision, and deep learning frameworks. The average salary for AI specialists in GCCs has risen to ₹18.5L annually, representing a 25% increase from the previous year.`,
      author: "Bootminds GCC Practice",
      date: DATA_AS_OF,
      readTime: 4,
      category: "talent",
      icon: "Brain",
      tags: ["AI", "talent", "bangalore", "growth"]
    },
    {
      title: "New Regulatory Framework for GCC Operations",
      summary: "Government introduces streamlined approval process for GCC establishments, reducing setup time by 40%.",
      fullContent: `The Indian government has introduced a comprehensive regulatory framework designed to streamline GCC operations and reduce bureaucratic hurdles. This new framework promises to cut setup time by 40% and simplify compliance requirements.\n\nKey changes include:\n• Single-window clearance system for GCC approvals\n• Digitized documentation processes\n• Reduced compliance reporting frequency\n• Simplified tax filing procedures\n• Enhanced intellectual property protection\n\nThe framework also introduces incentives for GCCs establishing operations in Tier-2 cities, including tax breaks and infrastructure support. Industry experts predict this will accelerate GCC growth in emerging markets like Kochi, Indore, and Coimbatore.`,
      author: "Bootminds GCC Practice",
      date: DATA_AS_OF,
      readTime: 5,
      category: "regulatory",
      icon: "FileText",
      tags: ["regulatory", "government", "compliance", "framework"]
    },
    {
      title: "Market Consolidation Trends in GCC Sector",
      summary: "Large enterprises are consolidating multiple GCC operations, creating mega-centers with 5000+ employees.",
      fullContent: `A significant trend toward consolidation is reshaping the GCC landscape, with major enterprises merging multiple smaller operations into mega-centers. This strategic shift is driven by operational efficiency and cost optimization goals.\n\nConsolidation benefits include:\n• Reduced operational overhead by 30-35%\n• Improved resource utilization and sharing\n• Enhanced career progression opportunities\n• Streamlined management structures\n• Better knowledge transfer and collaboration\n\nNotable examples include Microsoft's consolidation of three Bangalore centers into a single 8,000-employee facility and Goldman Sachs' merger of Mumbai and Pune operations. This trend is expected to continue as companies seek to maximize their Indian operations' strategic value.`,
      author: "Bootminds GCC Practice",
      date: DATA_AS_OF,
      readTime: 3,
      category: "market",
      icon: "TrendingUp",
      tags: ["consolidation", "efficiency", "strategy", "operations"]
    }
  ];

  // Mock data for talent pool analyzer
  const skillsData = [
    { name: "Software Development", value: 35 },
    { name: "Data Analytics", value: 25 },
    { name: "Cloud Computing", value: 20 },
    { name: "Cybersecurity", value: 12 },
    { name: "AI/ML", value: 8 }
  ];

  const salaryData = [
    { name: "Entry Level", value: 6 },
    { name: "Mid Level", value: 12 },
    { name: "Senior Level", value: 18 },
    { name: "Lead Level", value: 25 },
    { name: "Principal Level", value: 35 }
  ];

  const tabs = [
    { id: 'pulse', label: 'Market Pulse', icon: 'Activity' },
    { id: 'talent', label: 'Talent Landscape', icon: 'Users' },
    { id: 'regulatory', label: 'Regulatory Radar', icon: 'Shield' },
    { id: 'insights', label: 'Market Insights', icon: 'Lightbulb' }
  ];

  return (
    <>
      <Helmet>
        <title>Market Intelligence Hub - Real-Time Insights | Bootminds Elite</title>
        <meta name="description" content="Access real-time GCC market intelligence, regulatory updates, and talent landscape analysis. Your definitive source for strategic market insights." />
        <meta name="keywords" content="GCC market intelligence, regulatory updates, talent analytics, market insights, real-time data" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt content-animate">
          <div className="container-executive">
                          <div className="max-w-4xl mx-auto text-center space-y-8">
                <div className="flex items-center justify-center space-x-3 hero-title-animate">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Icon name="BarChart3" size={32} color="var(--color-primary)" />
                  </div>
                  <div className="text-left">
                    <h1 className="font-headline font-bold text-4xl lg:text-5xl text-text-primary">
                      Market Intelligence Hub
                    </h1>
                    <p className="text-xl text-text-secondary mt-2">
                      Real-Time Strategic Insights
                    </p>
                  </div>
                </div>
                
                <p className="text-lg text-text-secondary max-w-3xl mx-auto hero-description-animate">
                  Your definitive source for GCC market intelligence, regulatory updates, and competitive landscape analysis. 
                  Make informed decisions with real-time data and expert insights.
                </p>

                <div className="flex items-center justify-center flex-wrap gap-x-6 gap-y-2 text-sm text-text-secondary hero-description-animate">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>Data as of {DATA_AS_OF}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} />
                    <span>Sourced from NASSCOM-Zinnov, Mercer, Aon, Everest Group</span>
                  </div>
                </div>
              </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 border-b border-border content-section-animate">
          <div className="container-executive">
            <div className="flex flex-wrap justify-center gap-4">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover-lift ${
                    activeTab === tab.id
                      ? 'bg-cta text-cta-foreground shadow-md'
                      : 'bg-muted text-text-secondary hover:bg-muted/80 hover:text-text-primary'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Icon name={tab.icon} size={20} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content Sections */}
        <main className="py-12">
          <div className="container-executive">
            {activeTab === 'pulse' && (
              <div className="space-y-12">
                {/* Market Pulse Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {marketPulseData.map((data, index) => (
                    <div 
                      key={index} 
                      className="card-animate"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <MarketPulseCard {...data} />
                    </div>
                  ))}
                </div>

                {/* Talent Metrics Chart */}
                <TalentMetricsChart 
                  data={talentMetricsData} 
                  title="Talent Availability vs Market Demand"
                />

                {/* Location Comparison */}
                <LocationComparison locations={locationData} />
              </div>
            )}

            {activeTab === 'talent' && (
              <div className="space-y-12">
                {/* Talent Pool Analyzer */}
                <TalentPoolAnalyzer 
                  skillsData={skillsData} 
                  salaryData={salaryData} 
                />

                {/* Talent Metrics Chart */}
                <TalentMetricsChart 
                  data={talentMetricsData} 
                  title="City-wise Talent Distribution"
                />

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="card-elevated p-6 text-center">
                    <div className="w-16 h-16 gradient-executive rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Users" size={32} color="white" />
                    </div>
                    <h3 className="font-headline font-semibold text-2xl text-text-primary mb-2">{MARKET_OVERVIEW.totalEmployment.value}</h3>
                    <p className="text-text-secondary">{MARKET_OVERVIEW.totalEmployment.label}</p>
                    <p className="text-xs text-text-secondary/70 mt-2">Source: {MARKET_OVERVIEW.totalEmployment.source}</p>
                  </div>
                  <div className="card-elevated p-6 text-center">
                    <div className="w-16 h-16 gradient-accent rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="TrendingUp" size={32} color="white" />
                    </div>
                    <h3 className="font-headline font-semibold text-2xl text-text-primary mb-2">{MARKET_OVERVIEW.yoyHeadcountGrowth.value}</h3>
                    <p className="text-text-secondary">{MARKET_OVERVIEW.yoyHeadcountGrowth.label}</p>
                    <p className="text-xs text-text-secondary/70 mt-2">Source: {MARKET_OVERVIEW.yoyHeadcountGrowth.source}</p>
                  </div>
                  <div className="card-elevated p-6 text-center">
                    <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Award" size={32} color="white" />
                    </div>
                    <h3 className="font-headline font-semibold text-2xl text-text-primary mb-2">{MARKET_OVERVIEW.averageAttrition.value}</h3>
                    <p className="text-text-secondary">{MARKET_OVERVIEW.averageAttrition.label}</p>
                    <p className="text-xs text-text-secondary/70 mt-2">Source: {MARKET_OVERVIEW.averageAttrition.source}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'regulatory' && (
              <div className="space-y-12">
                {/* Regulatory Timeline */}
                <RegulatoryTimeline updates={regulatoryUpdates} />

                {/* Regulatory Impact Dashboard */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="card-elevated p-6">
                    <h3 className="font-headline font-semibold text-xl text-text-primary mb-2">
                      State Incentive Programs
                    </h3>
                    <p className="text-sm text-text-secondary mb-4">
                      Active GCC-friendly incentive programs across major Indian states (subsidies, stamp-duty waivers, power tariff rebates). For a current state-by-state matrix, see our <Link to="/resources/india-gcc-regulatory-guide" className="text-primary hover:underline">India GCC regulatory guide</Link>.
                    </p>
                    <ul className="space-y-2 text-sm text-text-primary">
                      <li className="flex items-start"><Icon name="MapPin" size={14} className="text-accent mr-2 mt-0.5" /> Karnataka — IT/BT-ITeS Policy, capital subsidy &amp; SGST reimbursement</li>
                      <li className="flex items-start"><Icon name="MapPin" size={14} className="text-accent mr-2 mt-0.5" /> Telangana — TS-iPASS single-window + GCC-specific incentives 2024</li>
                      <li className="flex items-start"><Icon name="MapPin" size={14} className="text-accent mr-2 mt-0.5" /> Tamil Nadu — IT Policy 2021; Tier-2 (Coimbatore, Hosur) capex support</li>
                      <li className="flex items-start"><Icon name="MapPin" size={14} className="text-accent mr-2 mt-0.5" /> Maharashtra — IT/ITeS Policy; SEZ &amp; STP incentives</li>
                      <li className="flex items-start"><Icon name="MapPin" size={14} className="text-accent mr-2 mt-0.5" /> Andhra Pradesh — IT Policy 4.0, Vizag IT SEZs</li>
                    </ul>
                    <p className="text-xs text-text-secondary/70 mt-4">Source: state IT/ITeS policy documents via Invest India</p>
                  </div>

                  <div className="card-elevated p-6">
                    <h3 className="font-headline font-semibold text-xl text-text-primary mb-2">
                      Regulatory landscape (active as of {DATA_AS_OF})
                    </h3>
                    <p className="text-sm text-text-secondary mb-4">
                      Key India statutes a GCC must operate under. We track changes via MCA, RBI, and CBIC notifications and update our regulatory guide quarterly.
                    </p>
                    <ul className="space-y-2 text-sm text-text-primary">
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> Companies Act, 2013 — entity formation &amp; ROC filings</li>
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> FEMA, 1999 + RBI Master Directions on FDI &amp; LRS</li>
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> Income Tax Act / Transfer Pricing (§ 92 series)</li>
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> CGST/SGST/IGST Acts — registration &amp; monthly returns</li>
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> DPDP Act, 2023 — data fiduciary obligations (in-force)</li>
                      <li className="flex items-start"><Icon name="FileText" size={14} className="text-accent mr-2 mt-0.5" /> Labour codes — phased state-by-state rollout</li>
                    </ul>
                    <p className="text-xs text-text-secondary/70 mt-4">Sources: MCA, RBI, CBIC, MeitY notifications</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'insights' && (
              <div className="space-y-8">
                {/* Market Insights */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    {marketInsights.map((insight, index) => (
                      <MarketInsightCard key={index} insight={insight} />
                    ))}
                  </div>
                  
                  {/* Sidebar */}
                  <div className="space-y-6">
                    <SubscriptionWidget />
                    
                    {/* Themes we're tracking */}
                    <div className="card-elevated p-6">
                      <h3 className="font-headline font-semibold text-lg text-text-primary mb-2">
                        Themes we&apos;re tracking
                      </h3>
                      <p className="text-xs text-text-secondary mb-4">Active research lines in the Bootminds GCC practice</p>
                      <div className="space-y-3">
                        {[
                          'AI/ML talent supply &amp; compensation curves',
                          'Tier-2 GCC viability (Coimbatore, Indore, Vizag, Lucknow)',
                          'DPDP Act compliance for India GCCs',
                          'Multi-city GCC operating models (BOT vs JV vs Owned)',
                          'Talent retention &amp; attrition patterns'
                        ].map((topic, index) => (
                          <div key={index} className="flex items-start p-2 rounded hover:bg-muted/30">
                            <Icon name="Target" size={14} className="text-accent mr-2 mt-1 flex-shrink-0" />
                            <span className="text-text-primary text-sm" dangerouslySetInnerHTML={{ __html: topic }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Sources & Methodology */}
        <section className="py-12 border-t border-border bg-background">
          <div className="container-executive">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-headline font-semibold text-2xl text-text-primary mb-2">Sources &amp; methodology</h2>
              <p className="text-sm text-text-secondary mb-6">
                Figures on this page are compiled from publicly available reports as of <strong>{DATA_AS_OF}</strong>. Tier-2 city
                figures (Coimbatore, Hosur, Indore, Vizag, Goa, Lucknow) are estimates derived from state IT/ITeS policy
                documents and Invest India city briefs; treat them as directional rather than survey-grade.
              </p>
              <ul className="grid md:grid-cols-2 gap-3 text-sm">
                {MARKET_DATA_SOURCES.map((s) => (
                  <li key={s.name} className="rounded-lg border border-border bg-card p-3">
                    <div className="font-medium text-text-primary">{s.name}</div>
                    <div className="text-xs text-text-secondary mt-1">{s.use}</div>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center">
                      Visit source <Icon name="ExternalLink" size={12} className="ml-1" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-muted/30">
          <div className="container-executive">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-headline font-bold text-3xl lg:text-4xl text-text-primary mb-6">
                Ready to Leverage Market Intelligence?
              </h2>
              <p className="text-lg text-text-secondary mb-8">
                Transform insights into strategic advantage with our expert consultation and market intelligence services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-cta hover:bg-cta/90 text-cta-foreground"
                  iconName="Calendar"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/executive-contact-strategic-engagement">
                    Schedule Strategic Consultation
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/resources">
                    Download Market Report
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-primary text-primary-foreground">
          <div className="container-executive">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} color="white" />
                  </div>
                  <div>
                    <div className="font-headline font-bold text-xl">Bootminds</div>
                    <div className="font-accent text-sm text-accent">Elite</div>
                  </div>
                </div>
                <p className="text-text-primary-foreground/80 text-sm">
                  Your trusted partner for GCC establishment and strategic market intelligence.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Intelligence Hub</h4>
                <ul className="space-y-2 text-sm text-primary-foreground/80">
                  <li><Link to="/market-intelligence" className="hover:text-accent transition-colors">Market Pulse</Link></li>
                  <li><Link to="/market-intelligence" className="hover:text-accent transition-colors">Talent Analytics</Link></li>
                  <li><Link to="/market-intelligence" className="hover:text-accent transition-colors">Regulatory Updates</Link></li>
                  <li><Link to="/market-intelligence" className="hover:text-accent transition-colors">Market Insights</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-sm text-primary-foreground/80">
                  <li><Link to="/services" className="hover:text-accent transition-colors">GCC Setup</Link></li>
                  <li><Link to="/about" className="hover:text-accent transition-colors">About Us</Link></li>
                  <li><Link to="/calculator" className="hover:text-accent transition-colors">ROI Calculator</Link></li>
                  <li><Link to="/case-studies" className="hover:text-accent transition-colors">Success Stories</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Connect</h4>
                <ul className="space-y-2 text-sm text-primary-foreground/80">
                  <li><Link to="/contact" className="hover:text-accent transition-colors">Contact Us</Link></li>
                  <li><Link to="/resources" className="hover:text-accent transition-colors">Resources</Link></li>
                  <li><a href="https://linkedin.com/company/bootminds-elite" className="hover:text-accent transition-colors">LinkedIn</a></li>
                  <li><a href="https://twitter.com/bootmindselite" className="hover:text-accent transition-colors">Twitter</a></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60">
              <p>&copy; {new Date().getFullYear()} Bootminds Elite. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MarketIntelligenceHub;